/**
 * RelatedQuotesTable - Display related quotes for a customer
 */

import React, { useCallback } from "react";
import { Table, Tag, Button, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const { Text, Link } = Typography;

const RelatedQuotesTable = ({ quotes = [] }) => {
  const navigate = useNavigate();

  const viewQuote = useCallback(
    (record) => {
      if (record.id) {
        navigate(`/quotes/${record.id}`);
      }
    },
    [navigate]
  );

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    return `R${value.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const columns = [
    {
      title: "Quote ID",
      dataIndex: "id",
      key: "id",
      render: (id, record) => (
        <Link
          onClick={(event) => {
            event.stopPropagation();
            viewQuote(record);
          }}
        >
          <Text code>{id || "N/A"}</Text>
        </Link>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (ref) => <Text code>{ref || "N/A"}</Text>,
    },
    {
      title: "From",
      dataIndex: "from",
      key: "from",
      render: (from) => from || "N/A",
    },
    {
      title: "To",
      dataIndex: "to",
      key: "to",
      render: (to) => to || "N/A",
    },
    {
      title: "Estimated Cost",
      dataIndex: "estimatedCost",
      key: "estimatedCost",
      align: "right",
      render: (cost) => cost ? formatCurrency(cost) : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          draft: { color: "default", text: "Draft" },
          pending: { color: "gold", text: "Pending" },
          sent: { color: "blue", text: "Sent" },
          accepted: { color: "green", text: "Accepted" },
          rejected: { color: "red", text: "Rejected" },
          expired: { color: "default", text: "Expired" },
          superseded: { color: "default", text: "Superseded" },
        };
        const cfg = statusMap[status] || { color: "default", text: status || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label="View quote"
          onClick={(event) => {
            event.stopPropagation();
            viewQuote(record);
          }}
          style={{ opacity: 0.6 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        />
      ),
    },
  ];

  if (quotes.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Text type="secondary">No quotes found for this customer</Text>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={quotes}
      rowKey={(record) => record.id}
      pagination={{ pageSize: 10 }}
      size="small"
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: () => viewQuote(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default RelatedQuotesTable;

